import { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { useNavigate } from "react-router-dom";
import { Upload, X } from "lucide-react";
import { Card } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import { useRcmImport } from "../../../../contexts/rcmImportContext";
import { validateFileSizeWithToast } from "../../../../lib/fileUploadValidation";

interface RcmUploadCardProps {
  // Rendered as a Cancel button when provided (the standalone upload page
  // wants one; the create-control page doesn't — switching cards is enough).
  onCancel?: () => void;
}

// The RCM spreadsheet dropzone + Continue step, shared between the standalone
// upload page and the create-control page's inline "Import from RCM" mode.
// Continue navigates into the column-mapping step; the parsed file travels
// via the app-level RcmImportProvider.
export const RcmUploadCard = ({ onCancel }: RcmUploadCardProps) => {
  const navigate = useNavigate();
  // `fileName` comes from the shared context, like `parsed`: with local
  // component state, a remount (switching create modes and back) showed an
  // empty dropzone while Continue stayed enabled from the stale `parsed`.
  const { setFileAndParse, parsed, fileName, loading, clear } = useRcmImport();

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!acceptedFiles || acceptedFiles.length === 0) return;
      const file = acceptedFiles[0];

      if (!validateFileSizeWithToast(file)) return;

      await setFileAndParse(file);
    },
    [setFileAndParse],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".xls"],
    },
    multiple: false,
  });

  const handleRemoveFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    clear();
  };

  return (
    <Card className="p-6">
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg min-h-[220px] flex flex-col items-center justify-center text-center p-6 cursor-pointer transition ${
          isDragActive ? "bg-stone-50 border-stone-400" : "border-stone-300"
        }`}
      >
        <input {...getInputProps()} />
        <Upload className="h-10 w-10 text-stone-400 mb-3" />
        <div className="font-medium mb-1 flex items-center gap-2">
          {fileName ? (
            <>
              <span>{fileName}</span>
              <button
                onClick={handleRemoveFile}
                className="p-1 hover:bg-stone-200 rounded-full transition"
                aria-label="Remove file"
              >
                <X className="h-4 w-4 text-stone-600" />
              </button>
            </>
          ) : (
            "Drag and drop your file here, or click to browse"
          )}
        </div>
        <div className="text-xs text-stone-500">
          We'll analyze your document and auto-detect common columns.
        </div>
      </div>
      <div className="bg-stone-50 border rounded mt-4 p-3 text-xs text-stone-700">
        What happens next? We’ll analyze your document and automatically detect
        columns like Control ID, Name, Description, Process, and more. You’ll be
        able to review and customize the mapping before importing.
      </div>
      <div className="flex justify-end gap-2 mt-4">
        {onCancel && (
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          onClick={() => navigate("/audit/create-control/map")}
          disabled={!parsed || loading}
          loading={loading}
        >
          Continue
        </Button>
      </div>
    </Card>
  );
};
